import { rem } from "@certn/cascadia/utils";
import {
  lineHeights,
  sizes,
  textStyles,
  weights,
} from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import parsePhoneNumber, {
  CountryCode,
  getCountryCallingCode,
  isSupportedCountry,
} from "libphonenumber-js";
import React, { useEffect, useRef, useState } from "react";
import {
  AriaTextFieldProps,
  useTextField,
  AriaTextFieldOptions,
} from "react-aria";
import { useTranslation } from "react-i18next";

import { PhoneCountriesSelect } from "./PhoneCountriesSelect";
import { useColors } from "../../../hooks/useColors";
import { getCurrentRegion } from "../../../utils";
import { FieldErrors } from "../_assets/input-wrapper/FieldErrors";
import { FormFeedback } from "../_assets/input-wrapper/FormFeedback";
import { FormLabel } from "../_assets/input-wrapper/FormLabel";
import { FormText } from "../_assets/input-wrapper/FormText";
import { InputWrapperProps } from "../_assets/input-wrapper/InputWrapper";

export interface PhoneNumber {
  number: string;
  national_number: string;
  country?: string;
  calling_code: string;
}

export type PhoneNumberValidity = "empty" | "valid" | "invalid";

export interface PhoneNumberInputProps
  extends Omit<AriaTextFieldProps, "onChange" | "value">,
    InputWrapperProps {
  name: string;
  label: string;
  info?: React.ReactNode;
  value?: PhoneNumber | string;
  defaultCountry?: CountryCode;
  onChange: (value: PhoneNumber | string | null) => void;
  onValidityChange?: (state: PhoneNumberValidity) => void;
  placeholder?: string;
  description?: string;
  errorMessage?: string | boolean | null;
  /** Shown when the number is invalid. Falls back to the generic phone error. */
  invalidMessage?: string;
  /** Accessible name for the country list. Replaces labelling it with the phone field. */
  countryAriaLabel?: string;
  successMessage?: string;
  testID?: string;
  type?: "string" | "object";
}

const COUNTRY_CODE_ALIASES: Record<string, CountryCode> = {
  UK: "GB",
};

const FALLBACK_COUNTRY: CountryCode = "CA";

function nationalDigits(phoneNumber: string, callingCode: string): string {
  const compact = phoneNumber.replace(/\D/g, "");
  const callingCodeDigits = callingCode.replace(/\D/g, "");
  if (callingCodeDigits && compact.startsWith(callingCodeDigits)) {
    return compact.slice(callingCodeDigits.length);
  }
  return compact;
}

const normalizeCountry = (
  country: string | undefined
): CountryCode | undefined => {
  if (!country) return undefined;
  const upper = country.toUpperCase();
  const aliased = COUNTRY_CODE_ALIASES[upper] ?? upper;
  return isSupportedCountry(aliased) ? (aliased as CountryCode) : undefined;
};

export function PhoneNumberInput(props: PhoneNumberInputProps) {
  const { t } = useTranslation();
  const {
    validationState,
    placeholder,
    label,
    info,
    value,
    defaultCountry:
      rawDefaultCountry = getCurrentRegion().toUpperCase() as CountryCode,
    onChange,
    onValidityChange,
    testID = "phone-number-input",
    type = "object",
    isDisabled,
    invalidMessage,
    countryAriaLabel,
    ...textFieldProps
  } = props;
  const defaultCountry: CountryCode =
    normalizeCountry(rawDefaultCountry) ?? FALLBACK_COUNTRY;
  const safeGetCallingCode = (countryCode: string | undefined): string => {
    const normalized = normalizeCountry(countryCode);
    if (normalized) {
      return `+${getCountryCallingCode(normalized)}`;
    }
    return `+${getCountryCallingCode(defaultCountry)}`;
  };
  const values = textStyles["input/text-field"];
  const [valid, setValid] = useState(true);
  const number = typeof value === "string" ? value : value?.number;
  const returnStringPhoneNumber = type === "string";
  const colors = useColors();
  const [country, setCountry] = useState<CountryCode>(() => {
    const normalizedNumber = number?.replace(/\s+/g, "") ?? "";
    const parsedPhone = parsePhoneNumber(normalizedNumber);
    const parsedCountry = normalizeCountry(parsedPhone?.country);

    if (parsedCountry) {
      return parsedCountry;
    }

    return defaultCountry;
  });
  const setSafeCountry = (next: string | undefined) => {
    const normalized = normalizeCountry(next);
    if (normalized) {
      setCountry(normalized);
    }
  };
  const ref = React.useRef(null);
  const [phoneNumber, setPhoneNumber] = useState(() => {
    const normalizedNumber = number?.replace(/\s+/g, "") ?? "";
    const parsed = parsePhoneNumber(normalizedNumber);

    if (parsed?.isValid()) {
      return parsed.formatInternational();
    }

    return number ?? "";
  });
  const showBuiltInError = !valid && phoneNumber.length > 5;
  const error = showBuiltInError || validationState === "invalid";
  const builtInErrorText =
    invalidMessage ?? t("Please enter a valid phone number.");
  const errorId = `${testID}-error`;
  const { labelProps, inputProps, descriptionProps } = useTextField(
    {
      ...textFieldProps,
      label,
      validationState: showBuiltInError ? "invalid" : validationState,
      value: typeof value === "string" ? value : value?.number,
    } as AriaTextFieldOptions<"input">,
    ref
  );

  // if you delete + sign phone_number won't error and will send empty string
  const ensurePlusSign = (number: string) =>
    !number.startsWith("+") ? "+" + number : number;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedInput = e.target.value
      .replace(/[^+\d\s]/g, "") // remove all characters besides numercial, spaces, and `+` for area code
      .replace(/(?!^)[+]/g, ""); // remove all `+` but the first one

    const isInternational = sanitizedInput.startsWith("+");
    let phone;

    if (isInternational) {
      phone = parsePhoneNumber(sanitizedInput);
      if (!phone?.isValid()) {
        phone = parsePhoneNumber(sanitizedInput, country);
      }
    } else {
      phone = parsePhoneNumber(sanitizedInput, country);
    }

    setSafeCountry(phone?.country ?? country);

    if (phone?.formatInternational() === phoneNumber) {
      setPhoneNumber(ensurePlusSign(sanitizedInput));
    } else {
      const number = phone?.isValid()
        ? phone?.formatInternational()
        : sanitizedInput;

      setPhoneNumber(ensurePlusSign(number));
    }
  };

  useEffect(() => {
    const phone = parsePhoneNumber(phoneNumber || "");

    const phoneWithCountry =
      !phone?.isValid() && country
        ? parsePhoneNumber(phoneNumber || "", country)
        : phone;

    const finalPhone = phoneWithCountry || phone;

    // A bare calling code (with or without a space) is not a number yet.
    // Compact values like "+1123456" still have national digits, so they must
    // fall through and be reported as invalid rather than empty.
    if (
      returnStringPhoneNumber &&
      nationalDigits(phoneNumber, safeGetCallingCode(country)).length === 0
    ) {
      onChange(null);
      setValid(true);
      onValidityChange?.("empty");
      return;
    }
    const isValidNumber = Boolean(finalPhone?.isValid());
    setValid(isValidNumber);
    if (isValidNumber && finalPhone) {
      if (finalPhone.country) {
        setSafeCountry(finalPhone.country);
      }
      // Emit canonical E.164 so the BE parser and FE parser agree on the input.
      const canonicalNumber = finalPhone.number ?? phoneNumber;
      if (returnStringPhoneNumber) {
        onChange(canonicalNumber);
      } else {
        onChange({
          number: canonicalNumber,
          national_number: finalPhone.nationalNumber,
          country: finalPhone.country,
          calling_code: finalPhone.countryCallingCode,
        });
      }
      onValidityChange?.("valid");
    } else {
      onChange(null);
      onValidityChange?.(
        nationalDigits(phoneNumber, safeGetCallingCode(country)).length === 0
          ? "empty"
          : "invalid"
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneNumber]);

  const prevCountry = useRef(country);
  const callingCode = safeGetCallingCode(country);

  useEffect(() => {
    const prevCallingCode = safeGetCallingCode(prevCountry.current);
    const callingCode = safeGetCallingCode(country);

    setPhoneNumber((number) => {
      return number.replace(prevCallingCode, callingCode);
    });

    prevCountry.current = country;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  const handleClick = () => {
    setPhoneNumber((number) => {
      return number ? number : `${callingCode} `;
    });
  };

  return (
    <div data-testid={`${testID}-container`}>
      <FormLabel {...labelProps} isRequired={props.isRequired} info={info}>
        {label}
      </FormLabel>
      <div className={styles.container}>
        <PhoneCountriesSelect
          labelID={labelProps.id}
          countryAriaLabel={countryAriaLabel}
          value={country}
          onSelectionChange={setSafeCountry}
          testID={`${testID}-country-code-select`}
          isDisabled={isDisabled}
        />
        <div className={css(styles.relativeBlock)} data-testid={testID}>
          <input
            {...inputProps}
            aria-invalid={showBuiltInError ? true : inputProps["aria-invalid"]}
            aria-describedby={
              [
                inputProps["aria-describedby"],
                props.description ? descriptionProps.id : null,
                showBuiltInError ? errorId : null,
              ]
                .filter(Boolean)
                .join(" ") || undefined
            }
            onClick={handleClick}
            data-testid={`${testID}-input`}
            value={phoneNumber}
            onChange={handleChange}
            onBlur={handleChange}
            placeholder={
              validationState === "invalid"
                ? ""
                : `${callingCode}  ${placeholder || ""}`
            }
            className={css(styles.input, {
              boxShadow: error
                ? `inset 0 0 0 1px ${colors["border-danger-input"]}`
                : props.isDisabled
                  ? `inset 0 0 0 1px ${colors["background-input-disabled"]}`
                  : `inset 0 0 0 1px ${colors["border-strong"]}`,
              border: isDisabled
                ? `1px solid ${colors["border-input-disabled"]}`
                : "none",
              color: isDisabled ? "var(--text-disabled)" : "var(--text-base)",
              fontSize: rem(sizes[values.size]),
              lineHeight: rem(lineHeights[values.lineHeight]),
              fontWeight: weights[values.weight],
              backgroundColor: props.isDisabled
                ? "var(--background-input-disabled)"
                : "var(--background-input)",
              "&:invalid": {
                border: `1px solid ${colors["border-danger-input"]}`,
              },
              "::placeholder": {
                color: "var(--text-placeholder)",
              },
              "&:hover:not(:focus)": isDisabled
                ? { borderColor: undefined }
                : !error
                  ? {
                      borderColor: "var(--border-input-active)",
                    }
                  : null,
              "&:focus": error
                ? css([
                    {
                      outline: "0",
                      borderColor: "var(--border-danger-input)",
                      background: "var(--background-base)",
                      boxShadow: "var(--focus-input-error)",
                    },
                  ])
                : css([
                    {
                      outline: "0",
                      borderColor: "var(--border-input-active)",
                      boxShadow: "var(--focus-input)",
                    },
                  ]),
            })}
            ref={ref}
          />
        </div>
      </div>
      <FormText {...descriptionProps}>{props.description}</FormText>
      <FieldErrors>{props.errorMessage}</FieldErrors>
      <div id={errorId} aria-live="polite">
        {showBuiltInError && !props.errorMessage ? (
          <FormFeedback type="invalid">{builtInErrorText}</FormFeedback>
        ) : null}
      </div>
      <FormFeedback type="valid">{props.successMessage}</FormFeedback>
    </div>
  );
}

const styles = {
  container: css({
    display: "flex",
    gap: "var(--space-2)",
  }),
  callingCode: css({
    position: "absolute",
    left: "var(--space-3)",
    top: "var(--space-2)",
  }),
  input: css({
    WebkitAppearance: "none",
    width: "100%",
    boxSizing: "border-box",
    paddingTop: "var(--space-2)",
    paddingRight: "var(--space-3)",
    paddingBottom: "var(--space-2)",
    paddingLeft: "var(--space-3)",
    borderRadius: "var(--border-radius-2)",
    fontFamily: "var(--font-family-default)",
  }),
  relativeBlock: css({
    position: "relative",
    flexGrow: 1,
  }),
};
